import { BaseEntity, ValidationResult, ValidationErrorInfo, ValidationErrorType } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { BCSaaSOrganizationSettingEntity } from "@bluecypress/bcsaas-entities";
import { IzzyMetadataEngine } from "./IzzyMetadataEngine.js";

/**
 * Extended OrganizationSettingEntity that adds ValidationRegex support.
 *
 * When saving an OrganizationSetting, this class validates the Value field
 * against the ValidationRegex defined on the associated Setting entity.
 */
@RegisterClass(BaseEntity, 'BC: Organization Settings')
export class OrganizationSettingEntityExtended extends BCSaaSOrganizationSettingEntity {

    /**
     * Override Validate to add ValidationRegex checking.
     *
     * This method:
     * 1. Calls super.Validate() to run existing validations (Scope/ChannelID logic)
     * 2. Looks up the Setting definition via IzzyMetadataEngine
     * 3. If the Setting has a ValidationRegex, validates the Value against it
     */
    override async ValidateAsync(): Promise<ValidationResult> {
        // Run base validations first (Scope/ChannelID logic from generated entity)
        const result = super.Validate();

        await IzzyMetadataEngine.Instance.Config(false, this.ContextCurrentUser);

        // Validate against the Setting's ValidationRegex if present
        this.ValidateAgainstSettingRegex(result);

        // Update Success based on errors
        result.Success = result.Success && (result.Errors.length === 0);

        return result;        
    }
    override get DefaultSkipAsyncValidation(): boolean {
        return false; // we want async validation here so we can ensure metadata engine is configured
    }

    /**
     * Validates the Value field against the Setting's ValidationRegex.
     *
     * @param result - the ValidationResult object to add any errors to
     */
    private ValidateAgainstSettingRegex(result: ValidationResult): void {
        // Skip if no SettingID is set yet
        if (!this.SettingID) {
            return;
        }

        // Skip if Value is null/undefined (other validators handle required check)
        if (this.Value === null || this.Value === undefined) {
            return;
        }

        // Look up the Setting definition from cached metadata
        const engine = IzzyMetadataEngine.Instance;

        const settingDef = engine.Settings.find(s => s.ID === this.SettingID);
        if (!settingDef) {
            // Setting definition not found - this shouldn't happen but skip validation
            return;
        }

        // Check if the Setting has a ValidationRegex
        if (!settingDef.ValidationRegex) {
            return;
        }

        // Validate the Value against the regex
        try {
            const regex = new RegExp(settingDef.ValidationRegex);
            const valueStr = String(this.Value);

            if (!regex.test(valueStr)) {
                result.Errors.push(new ValidationErrorInfo(
                    "Value",
                    `Value "${valueStr}" does not match the required format for setting "${settingDef.Name}". Expected pattern: ${settingDef.ValidationRegex}`,
                    this.Value,
                    ValidationErrorType.Failure
                ));
            }
        } catch (regexError) {
            // Invalid regex pattern in Setting definition - log but don't fail validation
            // This is a configuration error, not a user input error
            console.warn(
                `Invalid ValidationRegex pattern "${settingDef.ValidationRegex}" for setting "${settingDef.Name}": ${regexError}`
            );
        }
    }
}

/**
 * Tree-shaking prevention stub
 */
export function LoadOrganizationSettingEntityExtended(): void {
}
